import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import goldenRatio from '@glass/common/modules/goldenRatio/goldenRatio';
import { PURCHASE_ID_QUERY, RESUME_ID_QUERY } from '@glass/common/modules/pages/queryParams';
import shuffleSeed from '@glass/common/modules/utils/shuffleSeed';
import { CarouselProvider } from '@glass/web/components/Carousel/context/CarouselContext';
import TestimonialCarousel from '@glass/web/components/TestimonialSection/TestimonialCarousel';
import TotalRating from '@glass/web/components/TestimonialSection/TotalRating';
import { TESTIMONIAL_SECTION_TITLE } from '@glass/web/components/TestimonialSection/constants';
import { TESTIMONIAL_SECTION_ID } from '@glass/web/modules/content/pages/contentIds';
import { ALL_BREAKPOINTS } from '@glass/web/modules/theme/breakpointKeys';
import { DEFAULT_SPACING } from '@glass/web/modules/theme/spacing';

import Section from '@glass/shared/components/Section/Section';
import SectionButtonContinueCta from '@glass/shared/components/Section/SectionButtonContinueCta';
import SectionSpacer, { BOTTOM_EDGE } from '@glass/shared/components/Section/SectionSpacer';
import { DAILY_SEED } from '@glass/shared/modules/random/seeds';
import withTrackingContext from '@glass/shared/modules/tracking/withTrackingContext';

const getTestimonials = () =>
  import('@glass/web/components/TestimonialSection/mockTestimonials.json');

const TestimonialSection = React.forwardRef(
  (
    {
      type,
      spacerClassName,
      disableCta,
      className,
      ctaParams,
      ctaText,
      hasButtons,
      seed,
      isGrayBackground,
      isAlternatingColor,
      title,
      isLoading,
      outerSpacing,
      maxWidth,
    },
    ref,
  ) => {
    const [testimonials, setTestimonials] = useState(null);

    // todo: convert to react "use"?
    useEffect(() => {
      getTestimonials().then((module) => {
        setTestimonials(shuffleSeed.shuffle(module.default, seed));
      });
    }, [seed]);

    return (
      <Section
        ref={ref}
        disableCta
        className={className}
        ctaParams={ctaParams}
        ctaText={ctaText}
        isAlternatingColor={isAlternatingColor}
        isGrayBackground={isGrayBackground}
        loading={isLoading}
        maxWidth={maxWidth}
        outerSpacing={outerSpacing}
        subtitle={<TotalRating />}
        title={title}
        type={type}
      >
        <SectionSpacer edge={disableCta ? BOTTOM_EDGE : undefined}>
          <CarouselProvider initialSlideIdx={0}>
            <TestimonialCarousel
              hasButtons={hasButtons}
              isLoading={!testimonials}
              testimonials={testimonials}
            />
          </CarouselProvider>
        </SectionSpacer>
        {disableCta ? null : (
          <SectionSpacer
            className={spacerClassName}
            edge={BOTTOM_EDGE}
            spacing={DEFAULT_SPACING / goldenRatio}
          >
            <SectionButtonContinueCta disabled={isLoading} params={ctaParams}>
              {ctaText}
            </SectionButtonContinueCta>
          </SectionSpacer>
        )}
      </Section>
    );
  },
);

TestimonialSection.propTypes = {
  className: PropTypes.string,
  ctaParams: PropTypes.shape({
    [PURCHASE_ID_QUERY]: PropTypes.string,
    [RESUME_ID_QUERY]: PropTypes.string.isRequired,
  }),
  ctaText: PropTypes.string,
  disableCta: PropTypes.bool,
  hasButtons: PropTypes.bool,
  isAlternatingColor: PropTypes.bool,
  isGrayBackground: PropTypes.bool,
  isLoading: PropTypes.bool,
  maxWidth: PropTypes.oneOf(ALL_BREAKPOINTS),
  outerSpacing: PropTypes.number,
  seed: PropTypes.string,
  spacerClassName: PropTypes.string,
  title: PropTypes.string,
  type: PropTypes.string,
};

TestimonialSection.defaultProps = {
  hasButtons: true,
  disableCta: false,
  seed: DAILY_SEED,
  title: TESTIMONIAL_SECTION_TITLE,
  ctaText: '$(verbWords.singular) your $(resumeWords.singular) now',
};

export default React.memo(withTrackingContext(TestimonialSection, TESTIMONIAL_SECTION_ID));
